import { type PropsWithChildren, type ReactElement, useEffect, useMemo, useRef, useState } from 'react';
import { useRouter } from 'next/router';
import { type PageTrackingTypes } from '@pkgs/components/types/tracking';
import { useSetupScrollPositionStore } from '@pkgs/components/hooks/useSetupScrollPositionStore';
import { useScrollPositionStore } from '@pkgs/components/hooks/useScrollPositionStore';
import { type SearchMode } from '@pkgs/components/hooks/stores/search/slices/searchModeSlice';
import { type SearchListingsFilters, type GenericExposeListing, type MasterDataShopResponse } from '@pkgs/api';
import { type UseContactWizardArgs } from '@ev-tech/lead-flows';
import { datadogRum } from '@datadog/browser-rum';

import { type AppPageProps } from '@/pages/_app';
import { useHeaderHeight } from '@/hooks/useHeaderHeight';
import { HeaderBlock } from '@/components/ui-core-components-wrapper/Header/Header';
import { FooterBlock } from '@/components/ui-core-components-wrapper/Footer/Footer';
import { ExposeSkeletonLoading } from '@/components/property-expose/ExposeSkeletonLoading/ExposeSkeletonLoading';

import styles from './Layout.module.css';

type Props = PropsWithChildren<{
  isStickyHeader?: boolean;
  onContactCtaClick?: (args?: UseContactWizardArgs) => void;
  listing?: GenericExposeListing;
  pageTrackingTypes?: PageTrackingTypes;
  filters?: SearchListingsFilters;
  isPropertySearchPage?: boolean;
  onLanguageChange?: (locale: string) => void;
  isMapToggledOn?: boolean;
  searchMode?: SearchMode;
  shop?: MasterDataShopResponse;
}>;

const RoutePath = {
  EXPOSES: 'exposes',
  PRIVATE_OFFICE: 'private-office',
  PROJECTS: 'projects',
} as const;
type RoutePath = (typeof RoutePath)[keyof typeof RoutePath];

type AppLayoutProps = Props & Pick<AppPageProps, 'countryLinks' | 'spearHeadNavigation' | 'pageLanguages' | 'externalLinkDomains'>;

export const Layout = ({
  children,
  spearHeadNavigation,
  onLanguageChange,
  pageLanguages,
  isStickyHeader,
  onContactCtaClick,
  externalLinkDomains,
  listing,
  pageTrackingTypes,
  filters,
  isPropertySearchPage,
  countryLinks,
  isMapToggledOn,
  searchMode,
  shop,
}: AppLayoutProps): ReactElement => {
  const router = useRouter();
  const headerContainerRef = useRef<HTMLDivElement>(null);
  const [loadingRouteType, setLoadingRouteType] = useState<RoutePath | ''>('');
  const isHeaderVisible = useScrollPositionStore((state) => state.isHeaderVisible);

  const setScrollPosition = useScrollPositionStore((state) => state.setScrollPosition);

  useEffect(() => {
    const handleRouteChangeStart = (route: string) => {
      if (route) {
        const routeInLowerCase = route.toLowerCase();
        const possibleRoutes = [RoutePath.EXPOSES, RoutePath.PROJECTS];

        // Save scroll position when navigating away from propertysearch page
        if (router.pathname.includes('propertysearch') && window.scrollY !== 0) {
          setScrollPosition(window.scrollY);
        }

        const pageType = possibleRoutes.find((routePath) => routeInLowerCase.includes(routePath));

        if (pageType) {
          window.scrollTo({ top: 0, behavior: 'auto' });
          setLoadingRouteType(pageType);
        }
      }
    };

    const handleRouteChangeComplete = () => setLoadingRouteType('');

    const handleRouteChangeError = (url: string, err: Record<string, string>) => {
      datadogRum.addError(err, {
        extra: { message: `routing to ${url} threw an error` },
      });
      handleRouteChangeComplete();
    };

    router.events.on('routeChangeStart', handleRouteChangeStart);
    router.events.on('routeChangeComplete', handleRouteChangeComplete);
    router.events.on('routeChangeError', handleRouteChangeError);

    return () => {
      router.events.off('routeChangeStart', handleRouteChangeStart);
      router.events.off('routeChangeComplete', handleRouteChangeComplete);
      router.events.off('routeChangeError', handleRouteChangeError);
    };
  }, [router, setScrollPosition]);

  useSetupScrollPositionStore();

  const loadingComponent = useMemo(() => {
    if (
      loadingRouteType === RoutePath.EXPOSES ||
      loadingRouteType === RoutePath.PROJECTS ||
      loadingRouteType === RoutePath.PRIVATE_OFFICE
    ) {
      return <ExposeSkeletonLoading isPrivateOffice={loadingRouteType === RoutePath.PRIVATE_OFFICE} />;
    }
    return null;
  }, [loadingRouteType]);

  const layoutStyle = useHeaderHeight({
    elementClassName: '.sp-header',
    containerRef: headerContainerRef,
  });

  return (
    <div style={layoutStyle}>
      {spearHeadNavigation?.header && (
        <div
          className={styles.stickyContainer}
          ref={headerContainerRef}
          data-header-visible={isHeaderVisible}
          data-sticky={isPropertySearchPage ?? false}
        >
          <HeaderBlock
            blok={spearHeadNavigation.header}
            className="sp-header"
            pageLanguages={pageLanguages}
            onLanguageChange={onLanguageChange}
            isSticky={isStickyHeader}
            onContactCtaClick={onContactCtaClick}
            externalLinkDomains={externalLinkDomains}
            listing={listing}
            pageTrackingTypes={pageTrackingTypes}
            filters={filters}
            countryLinks={countryLinks}
            isMapToggledOn={isMapToggledOn}
            searchMode={searchMode}
            shop={shop}
          />
        </div>
      )}
      {loadingComponent ?? children}
      {spearHeadNavigation?.footer && (
        <FooterBlock
          blok={spearHeadNavigation.footer}
          externalLinkDomains={externalLinkDomains}
          listing={listing}
          pageTrackingTypes={pageTrackingTypes}
          filters={filters}
          isMapToggledOn={isMapToggledOn}
          searchMode={searchMode}
          shop={shop}
        />
      )}
    </div>
  );
};
